import {
  collection,
  query,
  where,
  getDocs,
  getCountFromServer,
  Firestore,
  Timestamp,
} from 'firebase/firestore';
import { PRODUCT_COLLECTION } from '../utills/productCollection';
import { RESTAURANT_COLLECTION } from '../utills/restaurantAdapter';
import { BRANCH_COLLECTION } from '../utills/branchAdapter';

// Latest orders collection used by Analytics & Dashboard.
const ORDERS_COLLECTION = 'zapee_restaurant_orders';

// Restaurant orders bill "service" as platformFee (+ surge); food orders use `service`.
// Summing is safe — the field that's absent is just 0.
const serviceOf = (charges: any): number =>
  toNum(charges?.service) + toNum(charges?.platformFee) + toNum(charges?.surge);

export interface TotalCounts {
  totalOrders: number;
  totalUsers: number;
  totalRestaurants: number;
  totalDeliveryPartners: number;
  totalBranches: number;
  totalProducts: number;
  totalSellers: number;
  totalCategories: number;
}

export interface DeliveredBreakdown {
  totalPrice: number;
  deliveryCharge: number;
  packing: number;
  service: number;
  tax: number;
  commission: number;
}

export interface DailyPoint {
  day: string;        // '01' … '31'
  revenue: number;
  delivered: number;
  cancelled: number;
  pending: number;
}

export interface MonthlyStats {
  deliveredCount: number;
  cancelledCount: number;
  pendingCount: number;
  totalCount: number;
  breakdown: DeliveredBreakdown;
  dailyData: DailyPoint[];
  avgOrderValue: number;
  netRevenue: number;          // totalRevenue - commission
}

export interface OrderCharges {
  deliveryCharge: number;
  packing: number;
  service: number;
  tax: number;
}

export interface LifetimeAmounts {
  totalOrderProductAmount: number;   // all orders totalPrice sum
  deliveredProductAmount: number;    // delivered orders totalPrice sum
  deliveredDeliveryCharge: number;
  deliveredPacking: number;
  deliveredTax: number;
  deliveredService: number;
  deliveredTotalRevenue: number;     // product + all charges (delivered)
  allOrdersTotalRevenue: number;     // product + all charges (all orders)
  totalOrderCount: number;
  deliveredOrderCount: number;
  avgOrderValue: number;             // allOrdersTotalRevenue / totalOrderCount
  avgDeliveredOrderValue: number;    // deliveredTotalRevenue / deliveredOrderCount
  avgDeliveryCharge: number;         // deliveredDeliveryCharge / deliveredOrderCount
}

// ── Lifetime amounts ──────────────────────────────────────────────
export async function getLifetimeAmounts(db: Firestore, branchId?: string): Promise<LifetimeAmounts> {
  const ordersRef = collection(db, ORDERS_COLLECTION);
  const baseQuery = branchId
    ? query(ordersRef, where('branchId', '==', branchId))
    : ordersRef;

  const snapshot = await getDocs(baseQuery);

  let totalOrderProductAmount = 0;
  let deliveredProductAmount = 0;
  let deliveredDeliveryCharge = 0;
  let deliveredPacking = 0;
  let deliveredTax = 0;
  let deliveredService = 0;
  let allDelivery = 0;
  let allPacking = 0;
  let allTax = 0;
  let allService = 0;
  let totalOrderCount = 0;
  let deliveredOrderCount = 0;

  snapshot.forEach(doc => {
    const d = doc.data();
    const price = toNum(d.totalPrice);
    const status = (d.status || '').toUpperCase();

    totalOrderCount++;
    totalOrderProductAmount += price;
    allDelivery += toNum(d.charges?.deliveryCharge);
    allPacking  += toNum(d.charges?.packing);
    allTax      += toNum(d.charges?.tax);
    allService  += serviceOf(d.charges);

    if (status === 'DELIVERED') {
      deliveredOrderCount++;
      deliveredProductAmount  += price;
      deliveredDeliveryCharge += toNum(d.charges?.deliveryCharge);
      deliveredPacking        += toNum(d.charges?.packing);
      deliveredTax            += toNum(d.charges?.tax);
      deliveredService        += serviceOf(d.charges);
    }
  });

  const allOrdersTotalRevenue = totalOrderProductAmount + allDelivery + allPacking + allTax + allService;
  const deliveredTotalRevenue = deliveredProductAmount + deliveredDeliveryCharge + deliveredPacking + deliveredTax + deliveredService;

  return {
    totalOrderProductAmount,
    deliveredProductAmount,
    deliveredDeliveryCharge,
    deliveredPacking,
    deliveredTax,
    deliveredService,
    deliveredTotalRevenue,
    allOrdersTotalRevenue,
    totalOrderCount,
    deliveredOrderCount,
    avgOrderValue: totalOrderCount > 0 ? allOrdersTotalRevenue / totalOrderCount : 0,
    avgDeliveredOrderValue: deliveredOrderCount > 0 ? deliveredTotalRevenue / deliveredOrderCount : 0,
    avgDeliveryCharge: deliveredOrderCount > 0 ? deliveredDeliveryCharge / deliveredOrderCount : 0,
  };
}

// ── Total counts ──────────────────────────────────────────────────
export async function getTotalCounts(db: Firestore): Promise<TotalCounts> {
  const [
    ordersCount, usersCount, restaurantsCount, deliveryPartnersCount,
    branchesCount, productsCount, sellersCount, categoriesCount,
  ] = await Promise.all([
    getCountFromServer(collection(db, ORDERS_COLLECTION)),
    getCountFromServer(collection(db, 'users')),
    getCountFromServer(collection(db, RESTAURANT_COLLECTION)),
    getCountFromServer(collection(db, 'zapee_delivery_partners')),
    getCountFromServer(collection(db, BRANCH_COLLECTION)),
    getCountFromServer(collection(db, PRODUCT_COLLECTION)),
    getCountFromServer(collection(db, 'Sellers')),
    getCountFromServer(collection(db, 'category')),
  ]);

  return {
    totalOrders: ordersCount.data().count,
    totalUsers: usersCount.data().count,
    totalRestaurants: restaurantsCount.data().count,
    totalDeliveryPartners: deliveryPartnersCount.data().count,
    totalBranches: branchesCount.data().count,
    totalProducts: productsCount.data().count,
    totalSellers: sellersCount.data().count,
    totalCategories: categoriesCount.data().count,
  };
}

// ── Monthly full stats ────────────────────────────────────────────
export async function getMonthlyStats(
  db: Firestore,
  startDate: Date,
  endDate: Date,
  branchId: string,
): Promise<MonthlyStats> {
  const ordersRef = collection(db, ORDERS_COLLECTION);

  // If branchId provided, filter by it; otherwise fetch all
  const baseQuery = branchId
    ? query(ordersRef, where('branchId', '==', branchId))
    : ordersRef;

  const snapshot = await getDocs(baseQuery);

  const startMs = startDate.getTime();
  const endMs = endDate.getTime();

  // Accumulators
  let deliveredCount = 0;
  let cancelledCount = 0;
  let pendingCount = 0;
  let totalCount = 0;
  let totalPrice = 0;
  let deliveryCharge = 0;
  let packing = 0;
  let service = 0;
  let tax = 0;

  const dailyMap: Record<string, DailyPoint> = {};

  snapshot.forEach(doc => {
    const data = doc.data();

    // Date filter
    const createdAt: Date | undefined =
      data.createdAt?.toDate?.() ?? (data.orderPlaceDate?.toDate?.());
    if (!createdAt) return;
    const ms = createdAt.getTime();
    if (ms < startMs || ms > endMs) return;

    totalCount++;
    const status: string = (data.status || '').toUpperCase();
    const day = String(createdAt.getDate()).padStart(2, '0');

    if (!dailyMap[day]) dailyMap[day] = { day, revenue: 0, delivered: 0, cancelled: 0, pending: 0 };

    if (status === 'DELIVERED') {
      deliveredCount++;
      dailyMap[day].delivered++;

      const price = toNum(data.totalPrice);
      totalPrice += price;
      dailyMap[day].revenue += price;

      const charges: OrderCharges = data.charges || {};
      deliveryCharge += toNum(charges.deliveryCharge);
      packing += toNum(charges.packing);
      service += serviceOf(charges);
      tax += toNum(charges.tax);
    } else if (status === 'CANCELLED' || status === 'CANCEL') {
      cancelledCount++;
      dailyMap[day].cancelled++;
    } else {
      pendingCount++;
      dailyMap[day].pending++;
    }
  });

  const commission = totalPrice * 0.1;
  const totalRevenue = totalPrice + deliveryCharge + packing + service + tax;
  const netRevenue = totalRevenue - commission;
  const avgOrderValue = deliveredCount > 0 ? totalRevenue / deliveredCount : 0;

  const dailyData = Object.values(dailyMap).sort((a, b) => a.day.localeCompare(b.day));

  return {
    deliveredCount,
    cancelledCount,
    pendingCount,
    totalCount,
    breakdown: { totalPrice, deliveryCharge, packing, service, tax, commission },
    dailyData,
    avgOrderValue,
    netRevenue,
  };
}

function toNum(v: any): number {
  if (typeof v === 'number') return isNaN(v) ? 0 : v;
  const n = parseFloat(String(v || '0'));
  return isNaN(n) ? 0 : n;
}

// ── Legacy: kept for backward compat ─────────────────────────────
export async function getDeliveredOrdersBreakdown(
  db: Firestore,
  startDate: Date,
  endDate: Date,
  branchId: string,
): Promise<DeliveredBreakdown> {
  const stats = await getMonthlyStats(db, startDate, endDate, branchId);
  return stats.breakdown;
}

export async function getDeliveredOrdersBreakdownClientFilter(
  db: Firestore, startDate: Date, endDate: Date, branchId: string,
): Promise<DeliveredBreakdown> {
  return getDeliveredOrdersBreakdown(db, startDate, endDate, branchId);
}
